import { Routes } from '@angular/router';
import { atAuthGuard, atGuestOnlyGuard } from '@anothertrip/anothertrip-ds';

import { STICKY_HEADER_DATA } from './components/header-wrapper/sticky-header';
import { fetchTaxProfileGuard } from './features/collaboration/guards/fetch-tax-profile.guard';
import { previewCollaborationGuard } from './features/collaboration/guards/preview-collaboration.guard';
import { requireNoTaxProfileGuard } from './features/collaboration/guards/require-no-tax-profile.guard';
import {
  publicCampaignAliasGuard,
  publicCampaignAliasMatch,
} from './features/marketplace/guards/public-campaign-alias.guard';
import { myCampaignsAuthGuard } from './features/marketplace/joined/guards/my-campaigns-auth.guard';
import { myCampaignsActiveGuard } from './features/marketplace/joined/my-campaigns.availability';
import { SUBSCRIPTION_ROUTES } from './features/subscription/subscription.routes';
import { TAX_AUDIT_ROUTES } from './features/tax-audit/routes/tax-audit.routes';
import { PageNotFoundPage } from './pages/page-not-found/page-not-found.page';
import { AUTH_ROUTES } from './routes/auth.routes';

const PAGE_NOT_FOUND_TITLE = 'Page not found';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./layouts/full-width/full-width.layout').then((c) => c.FullWidthLayout),
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/get-started/get-started.page').then((c) => c.GetStartedPage),
        canActivate: [atGuestOnlyGuard],
        data: STICKY_HEADER_DATA,
      },
      {
        path: 'get-started',
        loadComponent: () => import('./pages/get-started/get-started.page').then((c) => c.GetStartedPage),
        canActivate: [atGuestOnlyGuard],
        data: STICKY_HEADER_DATA,
      },
      // Kept as a real path: `public-campaign-alias.guard.ts` redirects a malformed campaign link here.
      {
        path: 'page-not-found',
        title: PAGE_NOT_FOUND_TITLE,
        component: PageNotFoundPage,
      },
      // Public-share alias for campaign deep-links. Renders inside the
      // FullWidthLayout (no dashboard chrome) so unauthenticated visitors
      // arriving via the operator's shared link see a clean marketing-style
      // page. The edge 301 from `www.anothertrip.com/campaigns/{id}` points
      // here. Both auth and unauth visitors share this route — the page
      // branches on auth state internally to pick the endpoint
      // (authed marketplace vs unauthed public) and the apply-CTA mode.
      // The guard only rejects malformed paths; the auth round-trip lives
      // inside the apply-CTA click handler, not the guard.
      //
      // NOTE: top-level path. If a future `/campaigns` index route ever
      // lands (e.g. a public marketplace browse), this entry must come
      // AFTER it in the children array — Angular evaluates routes in order
      // and the static `campaigns` index would otherwise never match.
      // Previously this lived under DashboardLayout where the parent's
      // path scoping made shadowing impossible.
      // `/campaigns/mine` (My campaigns) is refused by `publicCampaignAliasMatch` so it reaches the dashboard route below.
      {
        path: 'campaigns/:campaignId',
        loadComponent: () =>
          import('./features/marketplace/pages/campaign-share-view/campaign-share-view.page').then(
            (c) => c.CampaignShareViewPage
          ),
        canMatch: [publicCampaignAliasMatch],
        canActivate: [publicCampaignAliasGuard],
      },
      {
        path: 'subscription',
        loadComponent: () =>
          import('./features/subscription/layouts/subscription.layout').then((c) => c.SubscriptionLayout),
        canActivate: [atAuthGuard],
        canActivateChild: [atAuthGuard],
        children: SUBSCRIPTION_ROUTES,
      },
    ],
  },
  {
    path: '',
    loadComponent: () => import('./layouts/default/default.layout').then((c) => c.DefaultLayout),
    children: [
      {
        path: 'terms-of-use',
        loadComponent: () => import('./pages/terms-of-use/terms-of-use.page').then((c) => c.TermsOfUsePage),
      },
      {
        path: 'privacy-policy',
        loadComponent: () => import('./pages/privacy-policy/privacy-policy.page').then((c) => c.PrivacyPolicyPage),
      },
    ],
  },
  {
    path: '',
    loadComponent: () => import('./layouts/auth/auth.layout').then((c) => c.AuthLayout),
    canActivate: [atGuestOnlyGuard],
    children: AUTH_ROUTES,
  },
  {
    path: '',
    loadComponent: () => import('./layouts/dashboard/dashboard.layout').then((c) => c.DashboardLayout),
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('./pages/dashboard/dashboard.page').then((c) => c.DashboardPage),
        canActivate: [atAuthGuard],
        canActivateChild: [atAuthGuard],
        loadChildren: () => import('./routes/dashboard.routes').then((r) => r.DASHBOARD_ROUTES),
      },
      {
        path: 'account',
        loadComponent: () => import('./pages/account/account.page').then((c) => c.AccountPage),
        canActivate: [atAuthGuard],
      },
      // Legacy alias: the account screen was `/profile` before AT-2376. Keep the redirect so
      // bookmarks, Home readiness links captured earlier, and any external links still resolve.
      {
        path: 'profile',
        redirectTo: 'account',
        pathMatch: 'full',
      },
      {
        path: 'billing',
        title: 'Billing',
        loadComponent: () => import('./features/subscription/pages/billing/billing.page').then((c) => c.BillingPage),
        canActivate: [atAuthGuard],
      },
      {
        path: 'collaboration/preview/:collaborationId',
        loadComponent: () =>
          import('./features/collaboration/pages/collaboration-preview/collaboration-preview.page').then(
            (c) => c.CollaborationPreviewPage
          ),
        canActivate: [previewCollaborationGuard],
      },
      {
        path: 'collaboration/profile-error',
        loadComponent: () =>
          import('./features/collaboration/pages/tax-profile-error/tax-profile-error.page').then(
            (c) => c.TaxProfileErrorPage
          ),
        canActivate: [requireNoTaxProfileGuard],
      },
      {
        path: 'collaboration',
        loadComponent: () =>
          import('./features/collaboration/pages/collaboration/collaboration.page').then((c) => c.CollaborationPage),
        canActivate: [atAuthGuard, fetchTaxProfileGuard],
        loadChildren: () =>
          import('./features/collaboration/routes/collaboration.routes').then((r) => r.COLLABORATION_ROUTES),
      },
      {
        path: 'marketplace',
        loadChildren: () => import('./features/marketplace/marketplace.routes').then((r) => r.MARKETPLACE_ROUTES),
        canActivate: [atAuthGuard],
        canActivateChild: [atAuthGuard],
      },
      // My campaigns (campaign creator invitations, 2026-10-15): the campaigns a business put the
      // creator on, plus accepted applications. Always on — see my-campaigns.availability.ts. The
      // backend's invite email deep-links to /campaigns/mine/{campaignId}.
      {
        path: 'campaigns/mine',
        canMatch: [myCampaignsActiveGuard],
        canActivate: [myCampaignsAuthGuard],
        canActivateChild: [atAuthGuard],
        loadChildren: () => import('./features/marketplace/joined/joined.routes').then((r) => r.JOINED_ROUTES),
      },
      {
        path: 'tax-audit',
        canActivate: [atAuthGuard],
        canActivateChild: [atAuthGuard],
        children: TAX_AUDIT_ROUTES,
      },
    ],
  },
  {
    path: '',
    loadComponent: () => import('./layouts/full-screen/full-screen.layout').then((c) => c.FullScreenLayout),
    children: [
      {
        path: 'map/:requestId',
        loadComponent: () => import('./features/map/pages/map/map.page').then((c) => c.MapPage),
        canActivate: [atAuthGuard],
      },
    ],
  },
  // Any other address shows the not-found page where it is, rather than redirecting to /page-not-found, so the
  // address bar keeps what was typed or clicked: the visitor can spot a typo, and support can see which link broke.
  // It must stay the last group: Angular tries routes in order, and a `**` child of an earlier `''` group would
  // swallow every group after it.
  {
    path: '',
    loadComponent: () => import('./layouts/full-width/full-width.layout').then((c) => c.FullWidthLayout),
    children: [
      {
        path: '**',
        title: PAGE_NOT_FOUND_TITLE,
        component: PageNotFoundPage,
      },
    ],
  },
];
